import type {
  DashboardAuditLogEntry,
  DashboardCaseRow,
  DashboardCaseSearchRequest,
  DashboardCaseSearchResponse,
  DashboardChartResponse,
  DashboardChartSlice,
  DashboardSummaryKpi,
  DashboardSummaryResponse,
  DashboardVariant,
} from '../types/dashboard';
import { formatAuditDateTime, formatDocumentTableDateTime } from '../utils/dateFormat';
import { displayOrDash } from './taskListAdapter';
import { submittedAtColumnFilterToIso } from './tasks';

export type ApiDashboardKpiCard = {
  count?: number;
  label?: string;
  tooltip?: string;
  date_range_label?: string;
};

export type ApiDashboardSummaryResponse = {
  submitted?: ApiDashboardKpiCard;
  pending_assignment?: ApiDashboardKpiCard;
  assigned_in_progress?: ApiDashboardKpiCard;
  high_urgency_level?: ApiDashboardKpiCard;
  escalated?: ApiDashboardKpiCard;
  kpi_compliant?: ApiDashboardKpiCard;
  completed?: ApiDashboardKpiCard;
  from_date?: string;
  to_date?: string;
};

export type ApiChartBreakdownItem = {
  file_category_name?: string;
  sub_file_category_name?: string;
  count?: number;
  percentage?: number;
};

export type ApiUrgencyLevelSlice = {
  urgency_level_name?: string;
  count?: number;
  percentage?: number;
  file_categories?: ApiChartBreakdownItem[];
};

export type ApiTaskStatusSlice = {
  task_status_name?: string;
  count?: number;
  percentage?: number;
  file_categories?: ApiChartBreakdownItem[];
};

export type ApiFileCategorySlice = {
  file_category_name?: string;
  count?: number;
  percentage?: number;
  sub_categories?: ApiChartBreakdownItem[];
};

export type ApiUrgencyChartResponse = {
  urgency_levels?: ApiUrgencyLevelSlice[];
  total_count?: number;
};

export type ApiStatusChartResponse = {
  task_statuses?: ApiTaskStatusSlice[];
  total_count?: number;
};

export type ApiFileCategoryChartResponse = {
  file_categories?: ApiFileCategorySlice[];
  total_count?: number;
};

export type ApiDashboardCaseRow = {
  case_request_id?: string;
  task_id?: string;
  file_ref_no?: string;
  file_category?: string;
  sub_file_category?: string;
  submitted_agency?: string;
  submitted_at?: string;
  workflow_stage?: string;
  allocation_officer?: string;
  assigned_officer?: string;
  supervisor?: string;
  state_attorney?: string;
  task_status?: string;
  urgency_level?: string;
};

export type ApiDashboardCaseSearchPage = {
  content?: ApiDashboardCaseRow[];
  totalElements?: number;
  totalPages?: number;
  number?: number;
  size?: number;
};

export type ApiTimelineActivityBy = {
  name?: string;
  title?: string;
  designation?: string;
  current_role?: string;
};

export type ApiTimelineAction = {
  code?: string;
  display_name?: string;
};

export type ApiTimelineEntry = {
  id?: string;
  date_time?: string;
  action?: ApiTimelineAction;
  activity_by?: ApiTimelineActivityBy;
};

export type ApiTimelineResponse = {
  entries?: ApiTimelineEntry[];
  case_request_id?: string;
  total_count?: number;
};

const URGENCY_COLORS = ['#dc2626', '#f59e0b', '#16a34a', '#94a3b8'];

const URGENCY_COLOR_BY_LABEL: Record<string, string> = {
  high: URGENCY_COLORS[0],
  low: URGENCY_COLORS[1],
  medium: URGENCY_COLORS[2],
  unspecified: URGENCY_COLORS[3],
};

function urgencyColorForLabel(label: string, index: number): string {
  const key = label.trim().toLowerCase();
  return URGENCY_COLOR_BY_LABEL[key] ?? URGENCY_COLORS[index % URGENCY_COLORS.length];
}
const SG_STATUS_COLORS = ['#f59e0b', '#dc2626', '#f97316', '#3b82f6', '#16a34a'];
const OP_STATUS_COLORS = ['#dc2626', '#f97316', '#3b82f6', '#16a34a'];
const CATEGORY_COLORS = [
  '#3b82f6',
  '#8b5cf6',
  '#ec4899',
  '#14b8a6',
  '#f97316',
  '#a3e635',
  '#fb923c',
  '#94a3b8',
  '#06b6d4',
  '#f43f5e',
  '#64748b',
  '#6366f1',
];

const SG_SUMMARY_KPIS: Array<{ apiKey: keyof ApiDashboardSummaryResponse; key: string; color: DashboardSummaryKpi['color'] }> = [
  { apiKey: 'submitted', key: 'submitted', color: 'blue' },
  { apiKey: 'pending_assignment', key: 'pendingAssignment', color: 'amber' },
  { apiKey: 'assigned_in_progress', key: 'assignedInProgress', color: 'green' },
  { apiKey: 'high_urgency_level', key: 'highUrgency', color: 'red' },
  { apiKey: 'escalated', key: 'escalated', color: 'orange' },
  { apiKey: 'kpi_compliant', key: 'kpiCompliant', color: 'teal' },
  { apiKey: 'completed', key: 'completed', color: 'purple' },
];

const OPERATIONAL_SUMMARY_KPIS: Array<{ apiKey: keyof ApiDashboardSummaryResponse; key: string; color: DashboardSummaryKpi['color'] }> = [
  { apiKey: 'assigned_in_progress', key: 'assignedInProgress', color: 'green' },
  { apiKey: 'high_urgency_level', key: 'highUrgency', color: 'red' },
  { apiKey: 'escalated', key: 'escalated', color: 'orange' },
  { apiKey: 'kpi_compliant', key: 'kpiCompliant', color: 'teal' },
  { apiKey: 'completed', key: 'completed', color: 'purple' },
];

/** DataTable column key → CMS-SGD-5 request body filter field (snake_case). */
export const DASHBOARD_FILTER_COL_MAP: Record<string, string> = {
  fileRef: 'file_ref_no',
  fileCategory: 'file_category',
  subFileCategory: 'sub_file_category',
  submittedAgency: 'submitted_agency',
  workflowStage: 'workflow_stage',
  allocationOfficer: 'allocation_officer',
  assignedOfficer: 'assigned_officer',
  supervisor: 'supervisor',
  stateAttorney: 'state_attorney',
  taskStatus: 'task_status',
  urgencyLevel: 'urgency_level',
};

/** DataTable column key → CMS-SGD-5 sort_by value. */
export const DASHBOARD_SORT_COL_MAP: Record<string, string> = {
  fileRef: 'FILE_REF_NO',
  fileCategory: 'FILE_CATEGORY',
  subFileCategory: 'SUB_FILE_CATEGORY',
  submittedAgency: 'SUBMITTED_AGENCY',
  submittedAt: 'SUBMITTED_AT',
  workflowStage: 'WORKFLOW_STAGE',
  allocationOfficer: 'ALLOCATION_OFFICER',
  assignedOfficer: 'ASSIGNED_OFFICER',
  supervisor: 'SUPERVISOR',
  stateAttorney: 'STATE_ATTORNEY',
  taskStatus: 'TASK_STATUS',
  urgencyLevel: 'URGENCY_LEVEL',
};

function strField(row: Record<string, unknown>, ...keys: string[]): string {
  for (const key of keys) {
    const v = row[key];
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  return '';
}

function breakdownLines(items: ApiChartBreakdownItem[] | undefined, nameKey: 'file_category_name' | 'sub_file_category_name'): string[] {
  if (!items?.length) return [];
  return items.map((item) => {
    const name = (item[nameKey] ?? '').trim() || '—';
    const count = typeof item.count === 'number' ? item.count : 0;
    const pct = typeof item.percentage === 'number' ? Math.round(item.percentage) : 0;
    return `${name}: ${pct}% (${count.toLocaleString()})`;
  });
}

function chartSlices(
  rows: Array<{ label: string; value: number; breakdown?: string[] }>,
  colors: string[],
): DashboardChartSlice[] {
  return rows.map((row, index) => ({
    label: row.label,
    value: row.value,
    color: colors[index % colors.length],
    breakdown: row.breakdown,
  }));
}

export function adaptDashboardSummary(
  api: ApiDashboardSummaryResponse,
  variant: DashboardVariant,
): DashboardSummaryResponse {
  const defs = variant === 'sg' ? SG_SUMMARY_KPIS : OPERATIONAL_SUMMARY_KPIS;
  const kpis: DashboardSummaryKpi[] = defs.map(({ apiKey, key, color }) => {
    const card = api[apiKey] as ApiDashboardKpiCard | undefined;
    return {
      key,
      value: typeof card?.count === 'number' ? card.count : 0,
      color,
      tooltip: typeof card?.tooltip === 'string' ? card.tooltip : '',
    };
  });
  return { kpis };
}

export function adaptUrgencyChart(api: ApiUrgencyChartResponse): DashboardChartResponse {
  const rows = (api.urgency_levels ?? []).map((level) => ({
    label: (level.urgency_level_name ?? '').trim() || '—',
    value: typeof level.count === 'number' ? level.count : 0,
    breakdown: breakdownLines(level.file_categories, 'file_category_name'),
  }));
  return {
    slices: rows.map((row, index) => ({
      label: row.label,
      value: row.value,
      color: urgencyColorForLabel(row.label, index),
      breakdown: row.breakdown,
    })),
  };
}

export function adaptStatusChart(api: ApiStatusChartResponse, variant: DashboardVariant): DashboardChartResponse {
  const colors = variant === 'sg' ? SG_STATUS_COLORS : OP_STATUS_COLORS;
  const rows = (api.task_statuses ?? []).map((status) => ({
    label: (status.task_status_name ?? '').trim() || '—',
    value: typeof status.count === 'number' ? status.count : 0,
    breakdown: breakdownLines(status.file_categories, 'file_category_name'),
  }));
  return { slices: chartSlices(rows, colors) };
}

export function adaptFileCategoryChart(api: ApiFileCategoryChartResponse): DashboardChartResponse {
  const rows = (api.file_categories ?? []).map((category) => ({
    label: (category.file_category_name ?? '').trim() || '—',
    value: typeof category.count === 'number' ? category.count : 0,
    breakdown: breakdownLines(category.sub_categories, 'sub_file_category_name'),
  }));
  return { slices: chartSlices(rows, CATEGORY_COLORS) };
}

export function adaptDashboardCaseRow(row: ApiDashboardCaseRow): DashboardCaseRow {
  const record = row as Record<string, unknown>;
  const submittedRaw = strField(record, 'submitted_at', 'submittedAt');
  return {
    caseRequestId: strField(record, 'case_request_id', 'caseRequestId'),
    taskId: strField(record, 'task_id', 'taskId') || undefined,
    fileRef: displayOrDash(strField(record, 'file_ref_no', 'fileRefNo', 'file_ref')),
    fileCategory: displayOrDash(strField(record, 'file_category', 'fileCategory')),
    subFileCategory: displayOrDash(strField(record, 'sub_file_category', 'subFileCategory')),
    submittedAgency: displayOrDash(strField(record, 'submitted_agency', 'submittedAgency')),
    submittedAt: displayOrDash(formatDocumentTableDateTime(submittedRaw) || submittedRaw),
    workflowStage: displayOrDash(strField(record, 'workflow_stage', 'workflowStage')),
    allocationOfficer: displayOrDash(strField(record, 'allocation_officer', 'allocationOfficer')),
    assignedOfficer: displayOrDash(strField(record, 'assigned_officer', 'assignedOfficer')),
    supervisor: displayOrDash(strField(record, 'supervisor')),
    stateAttorney: displayOrDash(strField(record, 'state_attorney', 'stateAttorney')),
    taskStatus: displayOrDash(strField(record, 'task_status', 'taskStatus')),
    urgencyLevel: displayOrDash(strField(record, 'urgency_level', 'urgencyLevel')),
  };
}

export function adaptDashboardCaseSearchPage(api: ApiDashboardCaseSearchPage): DashboardCaseSearchResponse {
  const content = (api.content ?? []).map(adaptDashboardCaseRow);
  const totalElements = typeof api.totalElements === 'number' && api.totalElements >= 0 ? api.totalElements : content.length;
  const size = typeof api.size === 'number' && api.size > 0 ? api.size : content.length || 20;
  const totalPages =
    typeof api.totalPages === 'number' && api.totalPages > 0
      ? api.totalPages
      : totalElements === 0
        ? 1
        : Math.max(1, Math.ceil(totalElements / size));
  const page = typeof api.number === 'number' ? api.number + 1 : 1;

  return {
    content,
    page,
    size,
    totalElements,
    totalPages,
  };
}

function auditBadgeVariant(code: string, displayName: string): DashboardAuditLogEntry['badgeVariant'] {
  const s = `${code} ${displayName}`.toLowerCase();
  if (/escalat|reject|revert|overdue/.test(s)) return 'red';
  if (/update|edit|modify|change/.test(s)) return 'orange';
  if (/review|complete|approve|close/.test(s)) return 'green';
  if (/forward|assign|allocate|receive|create|submit|submitte/.test(s)) return 'blue';
  return 'yellow';
}

function activityRole(activity: ApiTimelineActivityBy | undefined): string {
  if (!activity) return '—';
  const parts = [activity.title, activity.designation, activity.current_role]
    .map((p) => (typeof p === 'string' ? p.trim() : ''))
    .filter(Boolean);
  return parts.length ? parts.join(' · ') : '—';
}

function splitAuditDateTime(value: string): { date: string; time: string } {
  const formatted = formatAuditDateTime(value);
  if (!formatted) return { date: '—', time: '—' };
  const comma = formatted.indexOf(',');
  if (comma < 0) return { date: formatted, time: '—' };
  return {
    date: formatted.slice(0, comma).trim() || '—',
    time: formatted.slice(comma + 1).trim() || '—',
  };
}

export function adaptTimelineEntry(entry: ApiTimelineEntry): DashboardAuditLogEntry {
  const action = entry.action ?? {};
  const code = (action.code ?? '').trim();
  const displayName = (action.display_name ?? '').trim();
  const name = (entry.activity_by?.name ?? '').trim() || 'System';
  const { date, time } = splitAuditDateTime(entry.date_time ?? '');

  return {
    date,
    time,
    name,
    role: activityRole(entry.activity_by),
    isSystem: !entry.activity_by?.name?.trim() || /^system$/i.test(name),
    action: displayName || code || 'Activity',
    badgeVariant: auditBadgeVariant(code, displayName),
  };
}

export function adaptTimelineResponse(api: ApiTimelineResponse): DashboardAuditLogEntry[] {
  return (api.entries ?? []).map(adaptTimelineEntry);
}

/** CMS-SGD-5 — build POST body from dashboard table state (page/size are query params). */
export function buildDashboardSearchBody(req: DashboardCaseSearchRequest): Record<string, string> {
  const body: Record<string, string> = {
    from: req.from,
    to: req.to,
    sort_by: DASHBOARD_SORT_COL_MAP[req.sortBy ?? 'fileRef'] ?? 'FILE_REF_NO',
    sort_direction: req.sortDirection ?? 'ASC',
  };

  if (req.columnFilters) {
    for (const [colKey, raw] of Object.entries(req.columnFilters)) {
      const trimmed = raw.trim();
      if (!trimmed) continue;

      if (colKey === 'submittedAt') {
        const pipe = trimmed.indexOf('|');
        const fromPart = pipe >= 0 ? trimmed.slice(0, pipe).trim() : trimmed;
        const toPart = pipe >= 0 ? trimmed.slice(pipe + 1).trim() : trimmed;
        if (fromPart) {
          const isoFrom = submittedAtColumnFilterToIso(fromPart);
          if (isoFrom) body.submitted_at_from = isoFrom.slice(0, 10);
        }
        if (toPart) {
          const isoTo = submittedAtColumnFilterToIso(toPart);
          if (isoTo) body.submitted_at_to = isoTo.slice(0, 10);
        }
        continue;
      }

      const apiField = DASHBOARD_FILTER_COL_MAP[colKey];
      if (apiField) body[apiField] = trimmed;
    }
  }

  return body;
}
